if (!customElements.get('floating-add-to-cart')) {
  customElements.define(
    'floating-add-to-cart',
    class FloatingAddToCart extends HTMLElement {
      constructor() {
        super();
        this.isDismissed = false;
        this.productInfoElement = null;
        this.scrollThreshold = 0;
        this.closeButton = this.querySelector('.floating-add-to-cart__close');
        this.productForm = this.querySelector('product-form');
        this.variantIdInput = this.querySelector('.product-variant-id');
        this.addButton = this.querySelector('.floating-add-to-cart__button');
        this.addButtonText = this.querySelector('.floating-add-to-cart__button-text');
      }

      connectedCallback() {
        // Only show on product detail pages
        if (!document.querySelector('product-info')) {
          return;
        }

        this.productInfoElement = document.querySelector('product-info');
        if (!this.productInfoElement) {
          return;
        }

        // Calculate scroll threshold (end of product info area)
        this.calculateScrollThreshold();

        // Set up scroll listener
        this.handleScroll = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.calculateScrollThreshold.bind(this), { passive: true });

        // Close button handler
        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.handleClose.bind(this));
        }

        // Listen for variant changes to update the form
        // Use pubsub system like other components
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeHandler = this.handleVariantChange.bind(this);
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.variantChangeHandler);

          // Listen for cart updates to handle button state
          this.cartUpdateHandler = this.handleCartUpdate.bind(this);
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.cartUpdateHandler);
        }

        // Initial check
        this.handleScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.calculateScrollThreshold);
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      calculateScrollThreshold() {
        if (!this.productInfoElement) return;

        // Find the main product info container
        const productInfoContainer = this.productInfoElement.querySelector('#ProductInfo-' + this.dataset.sectionId);
        if (!productInfoContainer) return;

        // Calculate when user has scrolled past the product info area
        // Use a small offset to trigger slightly before the end
        const rect = productInfoContainer.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        // Add some offset to show the floating cart when user scrolls past most of the product info
        this.scrollThreshold = scrollTop + rect.bottom - 200;
      }

      handleScroll() {
        if (this.isDismissed) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Recalculate threshold on resize (handled separately) but also check if it needs updating
        if (this.scrollThreshold === 0) {
          this.calculateScrollThreshold();
        }

        // Show when scrolled past product info area
        // Hide when scrolled back to top or near bottom
        if (scrollTop > this.scrollThreshold && scrollTop < documentHeight - windowHeight - 50) {
          this.show();
        } else {
          this.hide();
        }
      }

      show() {
        if (this.isDismissed) return;
        this.removeAttribute('hidden');
      }

      hide() {
        this.setAttribute('hidden', '');
      }

      handleClose() {
        this.isDismissed = true;
        this.hide();
      }

      handleVariantChange(event) {
        // Update variant ID in the form when variant changes
        // Event data is in event.data (not event.detail) per pubsub system
        const variant = event?.data?.variant;
        if (variant && this.variantIdInput) {
          this.variantIdInput.value = variant.id;
          
          // Update button availability
          const isAvailable = variant.available !== false;
          if (isAvailable) {
            this.addButton.removeAttribute('disabled');
            this.addButton.removeAttribute('aria-disabled');
            if (this.addButtonText) {
              this.addButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          } else {
            this.addButton.setAttribute('disabled', 'disabled');
            this.addButton.setAttribute('aria-disabled', 'true');
            if (this.addButtonText) {
              this.addButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }

          // Update price if needed
          const priceElement = this.querySelector('.floating-add-to-cart__price');
          if (priceElement && event?.data?.html) {
            const newPriceElement = event.data.html.querySelector(`#price-${this.dataset.sectionId}`);
            if (newPriceElement) {
              priceElement.innerHTML = newPriceElement.innerHTML;
            }
          }
        }
      }

      handleCartUpdate(event) {
        // Reset button state after cart update
        // Only reset if this component's form was the source
        if (this.addButton && event?.source === 'product-form') {
          this.addButton.classList.remove('loading');
          const spinner = this.addButton.querySelector('.loading__spinner');
          if (spinner) {
            spinner.classList.add('hidden');
          }
          // Don't remove disabled if variant is unavailable
          if (this.addButton.hasAttribute('disabled') && 
              this.addButton.getAttribute('disabled') === 'disabled') {
            // Keep disabled state
          } else {
            this.addButton.removeAttribute('aria-disabled');
          }
        }
      }
    }
  );
}